import { useState, useEffect, useContext } from 'react';
import {} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getYear } from 'date-fns';
import {
    AppContext,
    SttButtonFab,
    SttChipFacilityRow,
    SttTable,
    SttTopControls,
    SttPage,
} from '../../../sporttia/all';
import { useToggle } from '../../../lib/hooks';
import BusySchedule from './BusySchedule';
import { fullName } from '../../../lib/utils';
import { useBusySchedulesTour } from 'src/lib/tours/useBusySchedulesTour';
import { PERMISSIONS } from '../../../lib/permissions';

const statusFilters = {
    all: 'ALL',
    active: 'ACTIVE',
    inactive: 'INACTIVE',
    current: 'CURRENT',
};

const statusFilterValues = {
    ALL: 0,
    ACTIVE: 1,
    INACTIVE: 2,
    CURRENT: 3,
};

const statusFilterLabels: Record<number, string> = {
    0: 'ALL',
    1: 'ACTIVE',
    2: 'INACTIVE',
    3: 'CURRENT',
};

type Facility = {
    id: string | number;
    name: string;
};

type BusyScheduleType = {
    id: number;
    name: string;
    creator?: {
        name: string;
        surname1?: string;
        surname2?: string;
    };
    ini: string;
    end: string;
    numBusies: number;
    facilities: Facility[];
};

type BusySchedulesResponse = {
    rows: BusyScheduleType[];
    count: number;
    page: number;
    pages: number;
};

type Filters = {
    name: string;
    year: number;
    status: string;
};

type StatusFilter = {
    status: number;
};

export default function BusySchedules(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { startTour } = useBusySchedulesTour();

    const [busySchedules, setBusySchedules] = useState<BusySchedulesResponse>();
    const [busyScheduleId, setBusyScheduleId] = useState<string | null>(null);

    // Leer filtros guardados de sessionStorage
    const savedState = sessionStorage.getItem('busySchedulesState');
    const parsedState = savedState ? JSON.parse(savedState) : null;

    const [filters, setFilters] = useState<Filters>(
        parsedState?.filters ?? {
            name: '',
            year: getYear(new Date()),
            status: statusFilters.current,
        },
    );
    const [trash, toggleTrash] = useToggle(() => {
        loadBusySchedules();
    });
    // Variable para resolver el problema (chapuza) de Material UI que no se puede usar como valor en un selector un valor vacío.
    const [statusFilter, setStatusFilter] = useState<StatusFilter>(
        parsedState?.statusFilter ?? { status: statusFilterValues.CURRENT },
    );

    // Guardar filtros en sessionStorage cuando cambian
    useEffect(() => {
        sessionStorage.setItem(
            'busySchedulesState',
            JSON.stringify({ filters, statusFilter }),
        );
    }, [filters, statusFilter]);

    function loadBusySchedules(params = { page: 1, rows: 20 }): void {
        const filterParams: Record<string, unknown> = {
            name: filters.name,
            year: filters.year,
        };

        const statusString = statusFilterLabels[statusFilter.status];
        if (statusString === statusFilters.current) {
            filterParams.inTime = true;
        } else {
            filterParams.status =
                statusString === statusFilters.all ? '' : statusString;
        }

        cxt?.api('GET', `/scs/${cxt?.sc?.id}/busies/schedules`, {
            params: {
                ...params,
                ...filterParams,
                trash,
            },
            success: (response: BusySchedulesResponse) => {
                if (response.rows) {
                    setBusySchedules(response);
                }
            },
        });
    }

    function goCreateNew(): void {
        setBusyScheduleId('create');
    }

    return (
        <>
            <SttPage title={t('ClosedFacilities')} help={startTour}>
                <SttTopControls
                    p={2}
                    fields={[
                        {
                            caption: t('generic.name'),
                            name: 'name',
                            type: 'text',
                            xs: 12,
                            sm: 6,
                            md: 4,
                            lg: 3,
                            value: filters.name,
                            onEnterKey: loadBusySchedules,
                        },
                    ]}
                    extraFields={[
                        {
                            caption: t('generic.status'),
                            name: 'status',
                            type: 'select',
                            value: String(statusFilter.status),
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                            mandatory: false,
                            cleanable: false,
                            options: [
                                {
                                    label: t('generic.all'),
                                    value: 0,
                                },
                                {
                                    label: t('generic.active'),
                                    value: 1,
                                },
                                {
                                    label: t('generic.inactive'),
                                    value: 2,
                                },
                                {
                                    label: t('generic.current'),
                                    value: 3,
                                },
                            ],
                        },
                        {
                            caption: t('generic.year'),
                            name: 'year',
                            type: 'year',
                            value: filters.year,
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                            years: {
                                past: 5,
                                start: getYear(new Date()),
                                future: 5,
                            },
                        },
                    ]}
                    onChange={({
                        name,
                        value,
                    }: {
                        name: string | undefined;
                        value: unknown;
                    }) => {
                        if (!name) return;
                        if (name === 'status') {
                            setStatusFilter({
                                ...statusFilter,
                                status: value as number,
                            });
                        }
                        setFilters({ ...filters, [name]: value });
                    }}
                    onFilter={loadBusySchedules}
                    onToggleTrash={toggleTrash}
                />
                <SttTable
                    data={busySchedules}
                    onFetch={loadBusySchedules}
                    linkTo={(row: BusyScheduleType) =>
                        `/busy/schedules/${row.id}${trash ? '?trash=true' : ''}`
                    }
                    columns={[
                        {
                            title: t('generic.name'),
                            align: 'left',
                            field: 'name',
                        },
                        {
                            title: t('generic.creator'),
                            type: 'text',
                            value: (row: BusyScheduleType) =>
                                fullName(row?.creator),
                        },
                        {
                            title: t('generic.period'),
                            type: 'period',
                            value: (row: BusyScheduleType) => ({
                                ini: row.ini,
                                end: row.end,
                            }),
                        },
                        {
                            title: t('generic.periods'),
                            type: 'nonZero',
                            field: 'numBusies',
                        },
                        {
                            title: t('generic.facilities'),
                            type: 'text',
                            value: (row: BusyScheduleType) => (
                                <SttChipFacilityRow
                                    facilities={row.facilities}
                                    onFacilityClick={(facility: Facility) =>
                                        navigate(`/facilities/${facility.id}`)
                                    }
                                />
                            ),
                        },
                    ]}
                />
                <SttButtonFab
                    onClick={goCreateNew}
                    permission={PERMISSIONS.CLOSURE.CREATE}
                />
            </SttPage>

            {busyScheduleId && (
                <BusySchedule
                    idBusySchedule={busyScheduleId}
                    trash={trash}
                    onClose={() => {
                        setBusyScheduleId(null);
                        loadBusySchedules();
                    }}
                />
            )}
        </>
    );
}
